import { RefObject, useEffect, useLayoutEffect, useReducer, useRef } from "react";

export const COLLAPSE_HEIGHT_HOOK_DEFAULT_TRANSITION_DURATION = 600;

type CollapseState = {
    opened: boolean;
    animating: boolean;
};

type CollapseAction =
    | { type: "OPEN" }
    | { type: "CLOSE" }
    | { type: "TOGGLE" }
    | { type: "ANIMATION_END" };

const reducer = (state: CollapseState, action: CollapseAction): CollapseState => {
    switch (action.type) {
        case "OPEN":
            return { opened: true, animating: true };
        case "CLOSE":
            return { opened: false, animating: true };
        case "TOGGLE":
            return { opened: !state.opened, animating: true };
        case "ANIMATION_END":
            return { ...state, animating: false };
        default:
            return state;
    }
};

export const useCollapseHeight = (
    defaultOpened: boolean,
    defaultTransitionDuration: number = COLLAPSE_HEIGHT_HOOK_DEFAULT_TRANSITION_DURATION,
    active: boolean = true,
) => {
    const headerRef: RefObject<any> = useRef(null);
    const containerRef: RefObject<any> = useRef(null);
    const [state, dispatch] = useReducer(reducer, { opened: defaultOpened, animating: false });
    const activeRef = useRef(active);
    const skipAnimation = useRef(true);

    const open = () => dispatch({ type: "OPEN" });
    const close = () => dispatch({ type: "CLOSE" });

    useEffect(() => {
        activeRef.current = active;
    }, [active]);

    // Attach the header click listener and initialise the container height once,
    // synchronously after mount so the DOM is ready before any animation effect fires.
    useLayoutEffect(() => {
        const ref = headerRef.current;

        const handleHeaderClick = (e: MouseEvent) => {
            if (!activeRef.current) return;
            const target = e.target as HTMLElement;
            if (!target.closest("button")) {
                dispatch({ type: "TOGGLE" });
            }
        };

        if (ref) {
            ref.addEventListener("click", handleHeaderClick);
        }

        const container = containerRef.current;
        if (container && defaultOpened) {
            container.style.height = `${container.scrollHeight}px`;
        }

        return () => {
            if (ref) {
                ref.removeEventListener("click", handleHeaderClick);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Skip the initial run — animate only on subsequent open/close changes.
        if (skipAnimation.current) {
            skipAnimation.current = false;
            return;
        }

        const container = containerRef.current;
        if (!container) return;

        let transitionDuration = defaultTransitionDuration;
        if (container.offsetHeight < 150) {
            transitionDuration = 300;
        }

        const onTransitionEnd = () => dispatch({ type: "ANIMATION_END" });
        container.addEventListener("transitionend", onTransitionEnd);

        container.style.overflow = "hidden";
        container.style.transitionDuration = `${transitionDuration}ms`;
        container.style.transitionProperty = "height";
        container.style.transitionTimingFunction = "cubic-bezier(0.19, 0.9, 0.22, 0.9)";

        if (state.opened) {
            requestAnimationFrame(() => {
                container.style.height = "0px";

                requestAnimationFrame(() => {
                    container.style.height = `${container.scrollHeight}px`;
                });
            });
        } else {
            container.style.height = "0px";
        }

        return () => {
            container.removeEventListener("transitionend", onTransitionEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.opened]);

    return {
        opened: state.opened,
        animating: state.animating,
        headerRef,
        containerRef,
        open,
        close,
    };
};
